import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Header, Link, Table } from '@backstage/core-components';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { makeStyles, MenuItem, Select, FormControl, InputLabel } from '@material-ui/core';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles({
  container: {
    padding: '5px 40px',
    overflowY: 'auto',
  },
  greenButton: {
    color: 'green',
    cursor: 'pointer',
  },
  inProgressIcon: {
    color: 'orange',
  },
  dropdown: {
    minWidth: 200,
    marginBottom: 20,
  },
});

const JenkinsJobs = () => {
  const styles = useStyles();
  const identityApi = useApi(identityApiRef);
  const classes = useStyles();
  const [allJobs, setAllJobs] = useState<any[]>([]); // store all jobs
  const [rowData, setRowData] = useState<any[]>([]); // filtered jobs
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<number | 'all'>('all'); // default "all"
  const [buildInProgress, setBuildInProgress] = useState('');
  const discoveryApi = useApi(discoveryApiRef);
  const navigate = useNavigate();
  const fetchApi = useApi(fetchApiRef);
  const [role, setRole] = useState('');
  const [org, setOrg] = useState('');
  const [loading, setLoading] = useState(true);

  // fetch tenants list
  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await axios.get(`${baseUrl}/tenants`);
      setTenants(response.data);
      return response.data;
    } catch (e) {
      console.error('Error fetching tenants:', e);
      setTenants([]);
    }
  };

  // fetch all jenkins jobs (for all tenants)
  const fetchJobs = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const tenantId = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:'),
      )?.split(':')[1];

      const userRole = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('role:')
      )?.split(':')[1];
      console.log(userRole)
      setRole(userRole);
      const tenantsFromDb = await fetchTenantsFromDb();
      const tenant = tenantsFromDb.find(t => t.id.toString() === tenantId);
      setOrg(tenant.name);
      const baseurl = await discoveryApi.getBaseUrl('user');
      const fetchresponse = await fetchApi.fetch(`${baseurl}/jenkins/jobs`, {
        method: 'GET',
      });
      if (!fetchresponse.ok) {
        const errorText = await fetchresponse.text();
        throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
      }
      const data = await fetchresponse.json();
      setAllJobs(data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setAllJobs([]);
    }  finally {
      setLoading(false); // done loading
    }
  };

  // filter jobs by tenant whenever tenant changes
  useEffect(() => {
    if (selectedTenant === 'all') {
      setRowData(allJobs); // show all
    } else {
      const filtered = allJobs.filter(job => job.tenantId === selectedTenant);
      setRowData(filtered);
    }
  }, [selectedTenant, allJobs]);

  useEffect(() => {
    fetchTenantsFromDb();
    
    
    fetchJobs();
  }, []);

  const triggerBuild = async (jobName: string) => {
    try {
      const baseurl = await discoveryApi.getBaseUrl('user');
      setBuildInProgress(jobName);
      const fetchresponse = await fetchApi.fetch(`${baseurl}/trigger-build/${jobName}`, {
        method: 'POST',
      });
      if (!fetchresponse.ok) {
        const errorText = await fetchresponse.text();
        throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
      }
      const data = await fetchresponse.json();
      navigate('/build-logs', { state: { jobName, buildLogs: data } });
    } catch (error) {
      console.error('Error triggering build:', error);
    } finally {
      setBuildInProgress('');
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Status of Last Build',
      field: 'lastBuildStatus',
      render: (rowData: any) => {
        if (rowData.lastBuildStatus === 'SUCCESS') {
          return <Tooltip title="Success"><CheckCircleOutlineOutlinedIcon style={{ color: 'green' }} /></Tooltip>;
        } else if (rowData.lastBuildStatus === 'FAILURE') {
          return <Tooltip title="Failure"><CancelOutlinedIcon style={{ color: 'red' }} /></Tooltip>;
        } else if (rowData.lastBuildStatus === 'ABORTED') {
          return <Tooltip title="Aborted"><ErrorOutlineOutlinedIcon style={{ color: 'orange' }} /></Tooltip>;
        } else {
          return <Tooltip title="Unknown Status"><ErrorOutlineOutlinedIcon style={{ color: 'gray' }} /></Tooltip>;
        }
      },
    },
    {
      title: 'Name',
      field: 'name',
      render: (rowData: any) => <Link to={rowData.url} target="_blank">{rowData.name}</Link>,
    },
    { title: 'Last Success', field: 'lastSuccess' },
    { title: 'Last Failure', field: 'lastFailure' },
    { title: 'Last Duration', field: 'lastDuration' },
    {
      title: 'Build',
      field: 'name',
      render: (rowData: any) =>
        rowData.name === buildInProgress ? (
          <Tooltip title="Build in Progress">
            <CircularProgress size={20} className={`${classes.greenButton} ${classes.inProgressIcon}`} />
          </Tooltip>
        ) : (
          <Tooltip title="Schedule a build" placement="top">
            <PlayArrowOutlinedIcon className={classes.greenButton} onClick={() => triggerBuild(rowData.name)} />
          </Tooltip>
        ),
    },
  ], [classes.greenButton, classes.inProgressIcon, buildInProgress]);

  return (
    <div >
      {/* Header stays full width */}
      <Header title="Jenkins" subtitle={org} />
      {/* Dropdown below header, aligned right */}
      {role === "SuperAdmin" && (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 5, marginRight: 40 }}>
      <FormControl variant="outlined" size="small" className={classes.dropdown} style={{ marginTop: 12 }}>
        <Select
          value={selectedTenant}
          onChange={(e) => setSelectedTenant(e.target.value as number | 'all')}
          displayEmpty
        >
          <MenuItem value="all">All Tenants</MenuItem>
          {tenants.map((tenant) => (
            <MenuItem key={tenant.id} value={tenant.id}>
              {tenant.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      </div>
      )}
      {/* Jobs Table */}
      <div className={styles.container}>
        {!loading &&
        <Table
          options={{ paging: false }}
          data={rowData}
          columns={columns}
          title="Jenkins Jobs"
          emptyContent={null}
        />
      }
      </div>
  
      {!loading && rowData.length === 0 &&  (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>No data available</p>
        </div>
      )}
    </div>
  );
};

export default JenkinsJobs;
